/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useState, type ComponentPropsWithoutRef, type ReactElement, type ReactNode } from 'react';
import { NavLink } from '@/components/link';
import type { ShopperProducts } from '@/scapi';
import CategoryNavigationMenu, { WithCategoryNavigationMenu } from '@/components/navigation-menu-desktop';
import MobileMenuDropdown from '@/components/navigation-menu-mobile';
import { Button } from '@/components/buttons';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
// ## SEO url changes ##
import { toCategoryUrl, rewriteLegacyCategoryUrl } from '@/lib/seo/url-helpers';

function hasBanner(category?: ShopperProducts.schemas['Category']): category is ShopperProducts.schemas['Category'] {
    return (
        (typeof category?.c_headerMenuBanner === 'string' && category.c_headerMenuBanner.length > 0) ||
        (typeof category?.c_navigationBannerImage === 'string' && category.c_navigationBannerImage.length > 0)
    );
}

function CategoryBanner({
    category,
    ...props
}: ComponentPropsWithoutRef<'a'> & { category: ShopperProducts.schemas['Category'] }) {
    const config = useConfig<AppConfig>();
    const navigationCategory = category as ShopperProducts.schemas['Category'] & {
        c_navigationBannerImage?: string;
        c_navigationBannerLink?: string;
        c_navigationOverrideUrl?: string;
    };
    const bannerImage = navigationCategory.c_navigationBannerImage ?? '';
    const imageSrc = toImageUrl({ src: bannerImage, config });
    // ## SEO url changes ## — use allowOverrideCategoryUrls to control BM URL rewriting
    const allowOverride = config?.features?.allowOverrideCategoryUrls ?? false;
    const rawUrl = navigationCategory.c_navigationBannerLink ?? navigationCategory.c_navigationOverrideUrl;
    const categoryUrl = !rawUrl
        ? toCategoryUrl(config, category)
        : allowOverride
          ? rawUrl
          : rewriteLegacyCategoryUrl(rawUrl);

    return (
        <NavigationMenuLink asChild>
            <NavLink {...props} to={categoryUrl}>
                {imageSrc ? (
                    <img
                        className="object-contain w-full max-w-full max-h-[512px]"
                        src={imageSrc}
                        alt={category.name}
                    />
                ) : (
                    // eslint-disable-next-line react/no-danger
                    <div className="ml-auto" dangerouslySetInnerHTML={{ __html: bannerImage }} />
                )}
            </NavLink>
        </NavigationMenuLink>
    );
}

type Category = ShopperProducts.schemas['Category'];

export type NavigationMenuMegaSlots = {
    mobileNavigation: ReactNode;
    desktopNavigation: ReactNode;
};

type NavigationMenuMegaProps = Omit<ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu>, 'children'> & {
    children?: (slots: NavigationMenuMegaSlots) => ReactNode;
};

export function NavigationMenuMegaDesktop({ categories }: { categories: Category[] }): ReactElement {
    return (
        <div className="hidden lg:flex items-center w-full h-12 pt-4">
            <CategoryNavigationMenu
                categories={categories}
                delayDuration={0}
                propsViewport={() => ({
                    className:
                        'rounded-none border-0 border-b border-border shadow-lg [&[data-state=open]]:animate-[menuSlideDown_0.15s_ease-in] [&[data-state=closed]]:animate-none will-change-transform',
                    style: {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                    },
                })}
                propsContentContainer={() => ({
                    className: '!p-0 !animate-none !transition-none',
                })}
                propsContent={({ category }) => ({
                    className: cn(hasBanner(category) && 'justify-between'),
                })}
                propsElement={({ level }) => {
                    return { className: cn('text-sm', level < 2 && 'font-semibold') };
                }}
                renderSlotContentAfter={({ category }) => {
                    if (hasBanner(category)) {
                        return (
                            <aside className="self-stretch max-w-xs">
                                <CategoryBanner category={category} />
                            </aside>
                        );
                    }
                }}
            />
        </div>
    );
}

export function NavigationMenuMegaMobile({
    categories,
    isOpen,
    onOpenChange,
}: {
    categories: Category[];
    isOpen: boolean;
    onOpenChange: (isOpen: boolean) => void;
}): ReactElement {
    const { t } = useTranslation('header');

    return (
        <MobileMenuDropdown
            isOpen={isOpen}
            categories={categories}
            trigger={
                <Button
                    variant="ghost"
                    size="icon-sm"
                    type="button"
                    className="lg:hidden hover:bg-transparent hover:opacity-50 transition-opacity"
                    aria-label={isOpen ? t('closeMenu', 'Close menu') : t('openMenu', 'Open menu')}
                    aria-expanded={isOpen}>
                    {isOpen ? <X className="size-4" /> : <Menu className="size-4" />}
                </Button>
            }
            onOpenChange={onOpenChange}
            onClose={() => onOpenChange(false)}
        />
    );
}

/**
 * ResponsiveNavigationMenu resolves category data once and exposes dedicated mobile and desktop menu slots.
 *
 * @param props - Component props
 * @param props.resolve - Promise resolving to root categories and first-level subcategories
 * @param props.defer - Promise resolving to deeper subcategory data for prefetch
 * @param props.children - Optional render function for placing mobile and desktop slots in a parent layout
 * @returns Navigation slots from a shared category provider
 */
export default function ResponsiveNavigationMenu({
    resolve,
    defer,
    children,
    ...props
}: NavigationMenuMegaProps): ReactElement | null {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    if (!resolve) {
        return children ? <>{children({ mobileNavigation: null, desktopNavigation: null })}</> : null;
    }

    return (
        <WithCategoryNavigationMenu resolve={resolve} defer={defer} {...props}>
            {({ categories }) => {
                const mobileNavigation = (
                    <NavigationMenuMegaMobile
                        categories={categories}
                        isOpen={mobileMenuOpen}
                        onOpenChange={setMobileMenuOpen}
                    />
                );
                const desktopNavigation = <NavigationMenuMegaDesktop categories={categories} />;

                if (children) {
                    return children({ mobileNavigation, desktopNavigation });
                }

                return (
                    <>
                        {mobileNavigation}
                        {desktopNavigation}
                    </>
                );
            }}
        </WithCategoryNavigationMenu>
    );
}
